import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import TabbedPage from 'components/tabbed-page';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { refreshPendingCounts } from 'layouts/pending-counts';
import { usePolling } from 'pages/support-shared/utils';
import { FeedContext, FeedCtx } from './shared/context';
import { TabLabel } from './shared/components';
import { REPORT_REASON_FALLBACK } from './shared/labels';
import { FeedAccessView, FeedAdminMeta, FeedAdminSummary } from './shared/types';
import { errorCode, errorStatus, feedError, rankOf } from './shared/utils';
import PostsTab from './posts-tab';
import ReportsTab from './reports-tab';
import ReviewTab from './review-tab';
import LogsTab from './logs-tab';
import MembersTab from './members-tab';
import SettingsTab from './settings-tab';
import PostDrawer from './post-drawer';
import ComposerModal from './composer-modal';

/**
 * Trang Quản trị → Bảng tin (/feed) — docs/bang-tin/03-admin.md. ADMIN + mọi MOD; tab Thành viên
 * và Cài đặt MOD chỉ xem. Backend: hp-media-server-staging/src/feed (01-backend.md §6). Việc
 * không đủ quyền trả 409 FEED_ADMIN_ONLY / FEED_NO_ACCESS (không bao giờ 401/403 nghiệp vụ).
 */

type Phase = 'loading' | 'ready' | 'noModule' | 'noTables' | 'error' | 'denied';

const TITLE = 'Bảng tin';

const FeedManagerPage = () => {
  const [params, setParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('loading');
  const [loadError, setLoadError] = useState('');
  const [access, setAccess] = useState<FeedAccessView | null>(null);
  const [meta, setMeta] = useState<FeedAdminMeta | null>(null);
  const [summary, setSummary] = useState<FeedAdminSummary | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // ---------------------------------------------------------------- cổng truy cập (03 §5.1)

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    Promise.allSettled([
      AxiosUtils.get(API.feedMeAccess),
      AxiosUtils.get(API.feedAdminMeta),
      AxiosUtils.get(API.feedAdminSummary),
    ]).then(([a1, d1, d2]) => {
      if (!alive) return;
      const noAccess = [d1, d2].some((r) => r.status === 'rejected' && errorCode(r.reason) === 'FEED_NO_ACCESS');
      if (d1.status === 'fulfilled') setMeta(((d1.value as any)?.data as FeedAdminMeta) || null);
      if (d2.status === 'fulfilled') setSummary(((d2.value as any)?.data as FeedAdminSummary) || null);
      if (a1.status === 'rejected') {
        const err = a1.reason;
        const code = errorCode(err);
        const status = errorStatus(err);
        if (code === 'FEED_UNAVAILABLE' || status === 503) setPhase('noTables');
        else if (status === 404 && !code.startsWith('FEED_')) setPhase('noModule');
        else if (code === 'FEED_NO_ACCESS') setPhase('denied');
        else {
          setLoadError(feedError(err));
          setPhase('error');
        }
        return;
      }
      const view = (a1.value as any)?.data as FeedAccessView;
      setAccess(view);
      setPhase(!view?.canModerate || noAccess ? 'denied' : 'ready');
    });
    return () => {
      alive = false;
    };
  }, [attempt]);

  const loadSummary = useCallback(() => {
    AxiosUtils.get(API.feedAdminSummary)
      .then((res: any) => setSummary((res?.data as FeedAdminSummary) || null))
      .catch((err) => {
        if (errorCode(err) === 'FEED_NO_ACCESS') setPhase('denied');
      });
  }, []);

  // Huy hiệu và số trên nhãn tab: hỏi lại mỗi 60 giây khi tab trình duyệt đang hiện.
  usePolling(loadSummary, 60 * 1000, phase === 'ready');

  const reloadAccess = useCallback(async () => {
    try {
      const res: any = await AxiosUtils.get(API.feedMeAccess);
      const view = res?.data as FeedAccessView;
      if (view) {
        setAccess(view);
        if (!view.canModerate) setPhase('denied');
      }
    } catch {
      // Giữ quyền đã có; lần tải trang sau sẽ hỏi lại.
    }
  }, []);

  const afterAction = useCallback(() => {
    setRefreshKey((k) => k + 1);
    loadSummary();
    refreshPendingCounts();
  }, [loadSummary]);

  const openPost = useCallback(
    (id: number, drawerTab?: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('post', String(id));
          if (drawerTab) next.set('ptab', drawerTab);
          else next.delete('ptab');
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const closePost = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('post');
        next.delete('ptab');
        return next;
      },
      { replace: true }
    );
  }, [setParams]);

  const goTab = useCallback(
    (tab: string, extra?: Record<string, string>) => {
      const next = new URLSearchParams();
      if (tab !== 'posts') next.set('tab', tab);
      if (extra) Object.keys(extra).forEach((k) => extra[k] && next.set(k, extra[k]));
      setParams(next, { replace: true });
    },
    [setParams]
  );

  const reportReasons = useMemo(() => {
    const fromMeta = Array.isArray(meta?.reportReasons) ? meta?.reportReasons : null;
    const fromAccess = Array.isArray(access?.reportReasons) ? access?.reportReasons : null;
    return (fromMeta && fromMeta.length ? fromMeta : fromAccess && fromAccess.length ? fromAccess : REPORT_REASON_FALLBACK) as {
      value: string;
      label: string;
    }[];
  }, [meta, access]);

  const reportReasonLabel = useCallback(
    (value: string) => reportReasons.find((r) => r.value === value)?.label || value,
    [reportReasons]
  );

  const ctx: FeedCtx | null = useMemo(
    () =>
      access
        ? {
            access,
            meta,
            summary,
            isAdmin: access.tier === 'ADMIN',
            myRank: rankOf(access.tier),
            refreshKey,
            afterAction,
            openPost,
            goTab,
            reloadAccess,
            reportReasonLabel,
            reportReasons,
          }
        : null,
    [access, meta, summary, refreshKey, afterAction, openPost, goTab, reloadAccess, reportReasonLabel, reportReasons]
  );

  // ---------------------------------------------------------------- màn cổng

  if (phase !== 'ready' || !ctx) {
    let body: React.ReactNode;
    if (phase === 'loading') {
      body = (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <Spin />
        </div>
      );
    } else if (phase === 'noModule') {
      body = (
        <Result
          status="info"
          title="Máy chủ chưa có Bảng tin"
          subTitle="Backend đang chạy bản chưa có module Bảng tin (src/feed). Cập nhật backend rồi tải lại trang."
        />
      );
    } else if (phase === 'noTables') {
      body = (
        <Result
          status="warning"
          title="Chưa tạo bảng dữ liệu Bảng tin"
          subTitle="Máy chủ chưa chạy migration Bảng tin (feed). Báo người vận hành chạy migration rồi tải lại trang."
        />
      );
    } else if (phase === 'denied') {
      body = <Result status="403" title="Tài khoản này không có quyền kiểm duyệt Bảng tin." />;
    } else {
      body = (
        <Result
          status="error"
          title="Không tải được Bảng tin"
          subTitle={loadError}
          extra={<Button onClick={() => setAttempt((n) => n + 1)}>Thử lại</Button>}
        />
      );
    }
    return (
      <div>
        <PageTitle title={TITLE} />
        {body}
      </div>
    );
  }

  const reviewCount =
    (summary?.flaggedPosts ?? 0) +
    (summary?.flaggedComments ?? 0) +
    (summary?.pendingPosts ?? 0) +
    (summary?.pendingComments ?? 0);
  const postParam = Number(params.get('post')) || null;

  return (
    <FeedContext.Provider value={ctx}>
      <TabbedPage
        title={TITLE}
        extra={
          <>
            {summary?.overdueReports ? (
              <Button danger icon={<ExclamationCircleOutlined />} onClick={() => goTab('reports')}>
                {summary.overdueReports} báo cáo quá 24 giờ
              </Button>
            ) : null}
            {ctx.access.canPostAsPage && (
              <Button type="primary" icon={<EditOutlined />} onClick={() => setComposerOpen(true)}>
                Đăng bài HP Media
              </Button>
            )}
          </>
        }
        tabs={[
          { key: 'posts', label: 'Bài viết', render: () => <PostsTab /> },
          {
            key: 'reports',
            label: (
              <TabLabel text="Báo cáo" count={summary?.openReportTargets} danger={!!summary?.overdueReports} />
            ),
            render: () => <ReportsTab />,
          },
          { key: 'review', label: <TabLabel text="Cần xem" count={reviewCount} />, render: () => <ReviewTab /> },
          { key: 'logs', label: 'Nhật ký', render: () => <LogsTab /> },
          {
            key: 'members',
            label: <TabLabel text="Thành viên" count={summary?.memberCandidates} />,
            render: () => <MembersTab />,
          },
          { key: 'settings', label: 'Cài đặt', render: () => <SettingsTab /> },
        ]}
      />
      <PostDrawer postId={postParam} initialTab={params.get('ptab')} onClose={closePost} />
      <ComposerModal open={composerOpen} onClose={() => setComposerOpen(false)} />
    </FeedContext.Provider>
  );
};

export default FeedManagerPage;
